import type { StateCreator } from "zustand";
import type { JackpotCampaign } from "@/campaigns/modules/jackpot/types";
import type { JackpotProgressSchema } from "@/types";
import {
  campaignKey,
  matchesKey,
  type CampaignKey,
} from "@/campaigns/campaign-key";
import type { CampaignStreamState } from "./campaigns.store";
import type { AppStore } from "./app.store";

export interface JackpotSlice {
  /**
   * Assembled view objects — a two-way join (campaigns ⋈ jackpot_progress), rebuilt on either
   * changing. One entry per jackpot campaign, each carrying its one to four jackpots with their
   * volatile event state attached.
   */
  jackpotCampaigns: JackpotCampaign[];
  setJackpotCampaigns: (campaigns: JackpotCampaign[]) => void;
  clearJackpotCampaigns: () => void;

  /** Raw `jackpot_progress` stream, keyed by campaign key (the volatile tail). */
  jackpotProgressById: Record<CampaignKey, JackpotProgressSchema>;
  jackpotProgressStream: CampaignStreamState;
  setJackpotProgress: (rows: JackpotProgressSchema[]) => void;
  setJackpotProgressSubscribed: (subscribed: boolean) => void;
  setJackpotProgressLoading: (loading: boolean) => void;
  clearJackpotProgress: () => void;
}

const initialStream: CampaignStreamState = {
  subscribed: false,
  loading: false,
};

export const createJackpotSlice: StateCreator<JackpotSlice> = (set) => ({
  jackpotCampaigns: [],
  setJackpotCampaigns: (jackpotCampaigns) => set({ jackpotCampaigns }),
  clearJackpotCampaigns: () => set({ jackpotCampaigns: [] }),

  jackpotProgressById: {},
  jackpotProgressStream: initialStream,
  setJackpotProgress: (rows) =>
    set(() => {
      const byId: Record<CampaignKey, JackpotProgressSchema> = {};
      for (const row of rows) byId[campaignKey(row)] = row;
      return { jackpotProgressById: byId };
    }),
  setJackpotProgressSubscribed: (subscribed) =>
    set((state) => ({
      jackpotProgressStream: {
        ...state.jackpotProgressStream,
        subscribed,
        loading: false,
      },
    })),
  setJackpotProgressLoading: (loading) =>
    set((state) => ({
      jackpotProgressStream: { ...state.jackpotProgressStream, loading },
    })),
  clearJackpotProgress: () =>
    set({
      jackpotProgressById: {},
      jackpotProgressStream: initialStream,
    }),
});

/** Resolve a jackpot campaign by its custom_id or numeric id (as string). */
export function selectJackpotCampaignById(
  state: AppStore,
  id: string
): JackpotCampaign | undefined {
  return state.jackpotCampaigns.find((jc) => matchesKey(jc.campaign, id));
}
